import React from 'react'

import { Notification, Label } from '@pb/components'
import { useDecision } from '@optimizely/react-sdk'
import { useSelector, useDispatch } from 'react-redux'

import {
  operationSetPreviousFNSubscribeState,
  operationSetSendAgainAlreadyClicked,
} from 'App/duck/operations'
import appSelectors from 'App/duck/selectors'
import OPTIMIZELY from 'constants/optimizely'

import actions from '../../../duck/actions'
import settingsSelectors from '../../../duck/selectors'
import styles from './SettingsNotification.scss'

const SettingsNotification: React.FC = () => {
  const dispatch = useDispatch()

  const showEmailConfirmationSuccessNotification = useSelector(
    settingsSelectors.showEmailConfirmationSuccessNotification,
  )
  const showEmailConfirmationFailureNotification = useSelector(
    settingsSelectors.showEmailConfirmationFailureNotification,
  )
  const showSettingsUpdateFailureNotification = useSelector((state) =>
    settingsSelectors.showSettingsUpdateFailureNotification(state),
  )
  const showSettingsUpdateSuccessNotification = useSelector((state) =>
    settingsSelectors.showSettingsUpdateSuccessNotification(state),
  )
  const notificationMessageCopyKey = useSelector((state) =>
    settingsSelectors.settingsUpdateNotificationMessage(state),
  )
  const showSettingsDeleteFailureNotification = useSelector(
    settingsSelectors.showSettingsDeleteFailureNotification,
  )
  const notificationMessageDeleteCopyKey = useSelector((state) =>
    settingsSelectors.settingsDeleteNotificationMessage(state),
  )
  const showPhoneAsAltIdAddedNotification = useSelector((state) =>
    settingsSelectors.showPhoneAsAltIdAddedNotification(state),
  )
  const showPhoneAsAltIdRemovedNotification = useSelector((state) =>
    settingsSelectors.showPhoneAsAltIdRemovedNotification(state),
  )

  const showApiErrorNotification = useSelector(settingsSelectors.getApiErrors)
  const resetSettingsUpdateMessage = (): any => dispatch(actions.settingsUpdateMessageReset())
  const resetEmailConfirmation = (): any => dispatch(actions.resendEmailConfirmationReset())
  const resetSettingsDeleteMessage = (): any => dispatch(actions.settingsDeleteMessageReset())
  const resetApiErrorMessage = (): any => dispatch(actions.setApiErrorState(false))
  const resetPhoneAsAltidSettingMessage = (): any =>
    dispatch(actions.resetPhoneAsAltIdNotification())

  const [{ enabled: isFNBAnnerCampaignActive }] = useDecision(
    OPTIMIZELY.LCA_FASHION_NEWS_CAMPAIGN_BANNER,
  )

  const [{ enabled: isFNBAnnerDoubleOptiInCampaignActive }] = useDecision(
    OPTIMIZELY.LCA_FASHION_NEWS_CAMPAIGN_BANNER_DOUBLE_OPT_IN,
  )

  const userIsSubscribedToFashionNews = useSelector(appSelectors.getIsUserSubscribedToFashionNews)

  const previousFNSubscribeState = useSelector(appSelectors.getPreviousFNSubscribeState)

  const isDifferentFNSubscribeState = previousFNSubscribeState !== userIsSubscribedToFashionNews

  const isFNBannerVisible =
    userIsSubscribedToFashionNews && isFNBAnnerCampaignActive && isDifferentFNSubscribeState

  const shouldShowDoubleOptIn = useSelector(appSelectors.getShouldDisplayDoubleOptIn)

  const isSendAgainClickedForCurrentSession = useSelector(
    appSelectors.getIsSendAgainClickedForCurrentSession,
  )

  const isSendAgainAlreadyClickedForCurrentSession = useSelector(
    appSelectors.getIsSendAgainAlreadyClickedForCurrentSession,
  )

  const closeFNConfirmationBanner = (): void => {
    dispatch(operationSetPreviousFNSubscribeState(true))
    dispatch(operationSetSendAgainAlreadyClicked(false))
    resetSettingsUpdateMessage()
  }

  return (
    <>
      {showSettingsUpdateFailureNotification && (
        <Notification type="error" onExit={resetSettingsUpdateMessage}>
          <Label copyKey={notificationMessageCopyKey || `xxx.missing.generic.error.message.xxx`} />
        </Notification>
      )}

      {showSettingsUpdateSuccessNotification &&
        isFNBannerVisible &&
        isFNBAnnerDoubleOptiInCampaignActive &&
        !isSendAgainAlreadyClickedForCurrentSession && (
          <Notification
            type="success"
            onExit={() => closeFNConfirmationBanner()}
            shouldIgnoreTimeSpan
            className={styles.notification}
          >
            <Label copyKey="account.fn.campaign.successful.double.optin.first" />
            <Label copyKey="account.fn.campaign.successful.double.optin.second" />
          </Notification>
        )}

      {showSettingsUpdateSuccessNotification && isFNBannerVisible && !shouldShowDoubleOptIn && (
        <Notification
          type="success"
          onExit={() => closeFNConfirmationBanner()}
          shouldIgnoreTimeSpan
          className={styles.notification}
        >
          <Label copyKey="account.settings.saved.confirmation" />
          <Label copyKey="account.signup.fn.successful" />
        </Notification>
      )}
      {isSendAgainClickedForCurrentSession &&
        isSendAgainAlreadyClickedForCurrentSession &&
        isFNBAnnerDoubleOptiInCampaignActive &&
        shouldShowDoubleOptIn && (
          <Notification
            type="success"
            onExit={() => closeFNConfirmationBanner()}
            shouldIgnoreTimeSpan
            className={styles.notification}
          >
            <Label copyKey="account.fn.campaign.successful.double.optin.first" />
            <Label copyKey="account.fn.campaign.successful.double.optin.second" />
          </Notification>
        )}

      {isSendAgainClickedForCurrentSession &&
        isSendAgainAlreadyClickedForCurrentSession &&
        isFNBAnnerDoubleOptiInCampaignActive &&
        !shouldShowDoubleOptIn && (
          <Notification
            type="success"
            onExit={() => closeFNConfirmationBanner()}
            shouldIgnoreTimeSpan
            className={styles.notification}
          >
            <Label copyKey="account.settings.saved.confirmation" />
            <Label copyKey="account.signup.fn.successful" />
          </Notification>
        )}
      {isSendAgainClickedForCurrentSession &&
        isSendAgainAlreadyClickedForCurrentSession &&
        !isFNBAnnerDoubleOptiInCampaignActive &&
        shouldShowDoubleOptIn && (
          <Notification
            type="success"
            onExit={() => closeFNConfirmationBanner()}
            shouldIgnoreTimeSpan
            className={styles.notification}
          >
            <Label copyKey="account.fn.signup.successful.double.optin.first" />
            <Label copyKey="account.fn.signup.successful.double.optin.second" />
          </Notification>
        )}

      {isDifferentFNSubscribeState &&
        shouldShowDoubleOptIn &&
        !isFNBAnnerCampaignActive &&
        isFNBAnnerDoubleOptiInCampaignActive && (
          <Notification
            type="success"
            onExit={() => closeFNConfirmationBanner()}
            shouldIgnoreTimeSpan
            className={styles.notification}
          >
            <Label copyKey="account.fn.signup.successful.double.optin.first" />
            <Label copyKey="account.fn.signup.successful.double.optin.second" />
          </Notification>
        )}

      {showSettingsUpdateSuccessNotification &&
        shouldShowDoubleOptIn &&
        !isFNBAnnerCampaignActive &&
        isFNBAnnerDoubleOptiInCampaignActive && (
          <Notification
            type="success"
            onExit={() => closeFNConfirmationBanner()}
            shouldIgnoreTimeSpan
            className={styles.notification}
          >
            <Label copyKey="account.fn.signup.successful.double.optin.first" />
            <Label copyKey="account.fn.signup.successful.double.optin.second" />
          </Notification>
        )}

      {showSettingsUpdateSuccessNotification &&
        !isFNBannerVisible &&
        !isSendAgainAlreadyClickedForCurrentSession && (
          <Notification type="success" onExit={resetSettingsUpdateMessage}>
            <Label copyKey={notificationMessageCopyKey || `account.settings.saved.confirmation`} />
          </Notification>
        )}

      {showEmailConfirmationFailureNotification && (
        <Notification type="error" onExit={resetEmailConfirmation}>
          <Label copyKey="doi.personal.details.reminder.error" />
        </Notification>
      )}
      {showEmailConfirmationSuccessNotification && (
        <Notification type="info" onExit={resetEmailConfirmation}>
          <Label copyKey="doi.personal.details.reminder.confirm" />
        </Notification>
      )}
      {showSettingsDeleteFailureNotification && (
        <Notification type="error" onExit={resetSettingsDeleteMessage}>
          <Label copyKey={notificationMessageDeleteCopyKey} />
        </Notification>
      )}
      {showApiErrorNotification && (
        // Temporary used until account.api.general.error.message is in production
        <Notification type="error" onExit={resetApiErrorMessage}>
          <Label copyKey="checkout.error.general.retry" />
        </Notification>
      )}
      {showSettingsUpdateSuccessNotification && showPhoneAsAltIdAddedNotification && (
        <Notification
          type="info"
          onExit={resetPhoneAsAltidSettingMessage}
          shouldIgnoreTimeSpan
          className={styles.notification}
        >
          <Label copyKey="notification.successtoast.confirmchange" />
        </Notification>
      )}
      {showSettingsUpdateSuccessNotification && showPhoneAsAltIdRemovedNotification && (
        <Notification
          type="warning"
          onExit={resetPhoneAsAltidSettingMessage}
          shouldIgnoreTimeSpan
          className={styles.notification}
        >
          <Label copyKey="account.myDetails.altId.remove.message" />
        </Notification>
      )}
    </>
  )
}

export default SettingsNotification
